/**
 * OrgSwitcher — Dropdown in the top bar showing current org and allowing switch.
 *
 * Lists user's orgs from the auth store. Selecting one updates orgId.
 * "Create Organization" at bottom opens a simple inline form.
 */
import { useState, useRef, useEffect } from "react";
import { ChevronDown, Plus, Building2 } from "lucide-react";
import { useAuthStore } from "@/stores/useAuthStore";
import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/cn";

export function OrgSwitcher() {
  const { orgs, orgId, setOrg, loadOrgs } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const activeOrg = orgs.find((o) => o.id === orgId);

  /* Close dropdown on outside click */
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setCreating(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  async function handleCreate() {
    if (!newOrgName.trim()) return;
    const slug = newOrgName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

    try {
      const { data } = await authClient.organization.create({
        name: newOrgName.trim(),
        slug,
      });
      if (data) {
        await loadOrgs();
        setOrg(data.id);
      }
    } catch {
      /* ignore */
    }
    setNewOrgName("");
    setCreating(false);
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-white/5 hover:text-white"
      >
        <Building2 className="h-4 w-4" />
        <span className="max-w-[120px] truncate">
          {activeOrg?.name ?? "Select Org"}
        </span>
        <ChevronDown className="h-3.5 w-3.5" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-lg border border-white/10 bg-surface-raised py-1 shadow-card">
          {orgs.map((org) => (
            <button
              key={org.id}
              onClick={() => {
                setOrg(org.id);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-white/5",
                org.id === orgId
                  ? "text-primary"
                  : "text-muted-foreground hover:text-white",
              )}
            >
              <Building2 className="h-4 w-4 shrink-0" />
              <span className="truncate">{org.name}</span>
            </button>
          ))}

          {/* Divider */}
          <div className="my-1 h-px bg-white/10" />

          {creating ? (
            <div className="px-3 py-2">
              <input
                autoFocus
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="Organization name"
                className="w-full rounded-md border border-white/10 bg-surface px-2 py-1 text-sm text-white placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
              <div className="mt-2 flex justify-end gap-2">
                <button
                  onClick={() => setCreating(false)}
                  className="text-xs text-muted-foreground hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Create
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setCreating(true)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-white/5 hover:text-white"
            >
              <Plus className="h-4 w-4" />
              <span>Create Organization</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
